import { useLayoutEffect, useRef, useState } from 'react'

// Returns a font size (px) that makes a one-line value fit its container width, never larger than the
// element's natural CSS size. The hero figures are sized to look big on 1440p, but on 1080p the wider
// numbers (e.g. "DKK 109.1M") would otherwise wrap or overflow — so we shrink the font to fit.
//
// Width scales linearly with font size for a fixed string, so the fit size is just
// base × (available / naturalWidth). We measure a separate, full-size reference element holding the
// stable final value (not the live count-up text), so the size settles once per real change instead of
// thrashing each animation frame. Recomputes on container resize (fullscreen, resolution change) and
// after web fonts finish loading.
export function useFitText(text: string) {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  // null means "not measured yet" — fall back to the element's CSS-defined size until then.
  const [fontSizePx, setFontSizePx] = useState<number | null>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const recompute = () => {
      const available = container.clientWidth
      const natural = measure.scrollWidth
      const basePx = Number.parseFloat(getComputedStyle(measure).fontSize)
      if (!available || !natural || !basePx) return
      setFontSizePx(Math.min(basePx, (basePx * available) / natural))
    }

    recompute()

    // jsdom (tests) has no ResizeObserver; the one-shot recompute above is enough there.
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(recompute)
    observer.observe(container)
    // Web fonts can load after first paint and change the measured width.
    document.fonts?.ready?.then(recompute).catch(() => {})

    return () => observer.disconnect()
  }, [text])

  return { containerRef, measureRef, fontSizePx }
}
